import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Drawer from '@/components/base/Drawer';
import Badge, { statusTone } from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import { REQUEST_STATUSES } from '@/mocks/constants';
import type { QuoteRequest, RequestStatus } from '@/types';

export default function RequestDetail({
  request,
  onClose,
}: {
  request: QuoteRequest;
  onClose: () => void;
}) {
  const { setRequestStatus, addRequestActivity, setRequestVideoStatus } = useAppStore();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [videoViewed, setVideoViewed] = useState(
    request.activities.some((a) => a.text.toLowerCase().includes('incelendi')),
  );

  function handleStatus(status: RequestStatus) {
    setRequestStatus(request.id, status);
  }

  function handleVideoReview() {
    if (!videoViewed) {
      setVideoViewed(true);
      addRequestActivity(request.id, { time: 'Şimdi', text: 'Video incelendi' });
      if (request.status === 'Video Geldi') {
        setRequestStatus(request.id, 'Teklif Hazırlanacak');
      }
    }
  }

  function handleNote() {
    if (!note.trim()) return;
    addRequestActivity(request.id, { time: 'Şimdi', text: `Not: ${note.trim()}` });
    setNote('');
  }

  function handlePrepareQuote() {
    navigate('/teklifler', {
      state: {
        prefillCustomer: request.customerName,
        prefillService: request.service,
        prefillLocation: request.region,
      },
    });
  }

  return (
    <Drawer open onClose={onClose} title={request.id} width="max-w-2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="font-heading text-lg font-bold text-foreground-950">{request.customerName}</div>
            <div className="text-sm text-foreground-500">{request.service}</div>
          </div>
          <Badge tone={statusTone(request.status)}>{request.status}</Badge>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleVideoReview}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-primary-950 hover:bg-accent-400"
          >
            <i className="ri-video-line" /> Videoyu İncele
          </button>
          <button
            type="button"
            onClick={handlePrepareQuote}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            <i className="ri-file-add-line" /> Teklif Hazırla
          </button>
          <a
            href={`https://wa.me/90${request.phone.replace(/\s/g, '').slice(1)}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-whatsapp-line" /> WhatsApp
          </a>
          <a
            href={`tel:${request.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-phone-line" /> Ara
          </a>
        </div>

        {/* Customer info */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Müşteri Bilgileri</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Telefon</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.phone}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Bölge</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.region}</div>
            </div>
          </div>
          {request.addressNote && (
            <p className="mt-2 text-sm text-foreground-500">Adres: {request.addressNote}</p>
          )}
        </div>

        {/* Request info */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Talep</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Hizmet</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.service}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Kaynak</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.source}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Talep Tarihi</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.requestDate}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Mülk Tipi</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.propertyType}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Yaklaşık Alan</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.area}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Sorumlu</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{request.responsible}</div>
            </div>
          </div>
          {request.customerNote && (
            <p className="mt-2 rounded-lg bg-background-50 p-3 text-sm text-foreground-600">
              <span className="font-semibold text-foreground-800">Müşteri notu: </span>
              {request.customerNote}
            </p>
          )}
        </div>

        {/* Video */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Video</h4>
          <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="relative flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary-950">
              <i className="ri-play-circle-fill text-3xl text-accent-400" />
              <span className="absolute bottom-1 right-1 rounded bg-primary-950/70 px-1 text-[10px] font-semibold text-white">
                Demo
              </span>
            </div>
            <div className="flex-1">
              <Badge tone={statusTone(request.videoStatus)}>{request.videoStatus}</Badge>
              <p className="mt-1.5 text-xs text-foreground-500">
                Gerçek video yüklenmedi — görsel yer tutucu olarak gösteriliyor.
              </p>
              {request.videoStatus !== 'Video Geldi' && (
                <button
                  type="button"
                  onClick={() => setRequestVideoStatus(request.id, 'Video Geldi')}
                  className="mt-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-100 px-2.5 py-1.5 text-xs font-semibold text-accent-800 hover:bg-accent-200"
                >
                  <i className="ri-upload-cloud-2-line" /> Video Geldi İşaretle
                </button>
              )}
              {videoViewed && (
                <p className="mt-1 text-xs font-semibold text-emerald-600">
                  <i className="ri-check-line" /> Video incelendi
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Status change */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Durum Güncelle</h4>
          <select
            className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            value={request.status}
            onChange={(e) => handleStatus(e.target.value as RequestStatus)}
          >
            {REQUEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Activity timeline */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Aktivite Geçmişi</h4>
          <ul className="space-y-2">
            {request.activities.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 w-24 shrink-0 text-xs font-semibold text-foreground-400">{a.time}</span>
                <span className="relative flex-1 border-l border-background-200 pl-4">
                  <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-accent-500" />
                  <span className="text-foreground-700">{a.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Add note */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Not Ekle</h4>
          <div className="flex gap-2">
            <input
              className="h-10 flex-1 rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Not ekle..."
            />
            <button
              type="button"
              onClick={handleNote}
              className="rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              Ekle
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}